import { validationConstants } from "constants/formValidation";
import { orderStatusConstants } from "constants/order";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { getOrderStatus } from "pages/CreateOrder/utils/helpers";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";
import { SelectButton } from "primereact/selectbutton";
import { useEffect } from "react";
import { Controller, useFormContext, FieldValues } from "react-hook-form";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IOrder } from "types/Order/Order";

import {
  redoOrderSMSTemplate,
  redoOrderNoBalanceDueSMSTemplate,
  createOrderSMSTemplate,
} from "./helpers";

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(4)};
`;

export interface ICommunicationFields {
  textMessage: string;
}

const options = [
  {
    label: "Send Default Text",
    value: true,
  },
  {
    label: "Send Custom Text",
    value: false,
  },
];

export function Communication({
  account,
  redo,
  draft,
  order,
}: {
  account: IAccount;
  redo?: boolean;
  draft?: boolean;
  order?: IOrder;
}) {
  const {
    control,
    trigger,
    formState: { errors, dirtyFields },
    setValue,
    watch,
  } = useFormContext<FieldValues>();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();

  const orderTypefield = watch("orderType");
  const defaultText = watch("sendDefaultText");
  const customText = watch("customText");
  const preferredContactMethod = watch("preferredContactMethod");
  const orderTypeField = watch("orderType");
  const amountField = watch("amount");
  const totalAmountField = watch("totalAmount");
  const scheduledCommunicationDate = watch("scheduledFirstCommunicationDate");
  const scheduledFirstCommunicationSent = watch(
    "scheduledFirstCommunicationSent"
  );
  const scheduledCommunicationCustomText = watch(
    "scheduledCommunicationCustomText"
  );

  useEffect(() => {
    const orderStatus = getOrderStatus({
      amount: amountField,
      totalAmount: totalAmountField,
      orderType: orderTypeField,
    });
    const communicationTemplatesForOrderType =
      account?.communicationTemplates?.find((template) => {
        return template.orderType === orderTypefield;
      });
    const textMessage =
      orderStatus === orderStatusConstants.NO_BALANCE_DUE
        ? createOrderSMSTemplate
        : communicationTemplatesForOrderType?.smsTemplate;
    const userHasEdited = !!dirtyFields?.customText;

    if (redo) {
      const messageTemaple =
        orderStatus === orderStatusConstants.NO_BALANCE_DUE
          ? redoOrderNoBalanceDueSMSTemplate
          : redoOrderSMSTemplate;
      let customMessage = messageTemaple;
      if (scheduledCommunicationDate && !scheduledFirstCommunicationSent) {
        customMessage =
          customText || scheduledCommunicationCustomText || textMessage;
      }
      if (
        order?.scheduledFirstCommunicationDate &&
        !scheduledFirstCommunicationSent &&
        !scheduledCommunicationDate
      ) {
        customMessage = textMessage || "";
      }
      setValue("customText", customMessage);
      return;
    }
    if (draft) {
      if (scheduledCommunicationDate && !scheduledFirstCommunicationSent) {
        setValue(
          "customText",
          customText || scheduledCommunicationCustomText || textMessage
        );
        return;
      }
      if (
        order?.scheduledFirstCommunicationDate &&
        !scheduledFirstCommunicationSent &&
        !scheduledCommunicationDate
      ) {
        setValue("customText", textMessage);
        return;
      }
    }
    if (userHasEdited) {
      return;
    }
    setValue("customText", textMessage);
  }, [
    orderTypefield,
    account,
    redo,
    amountField,
    totalAmountField,
    orderTypeField,
    scheduledCommunicationDate,
    scheduledFirstCommunicationSent,
    scheduledCommunicationCustomText,
    draft,
    order,
    dirtyFields.customText,
  ]);

  useEffect(() => {
    if (preferredContactMethod === "Email") {
      setValue("sendDefaultText", true);
    }
  }, [preferredContactMethod]);

  const optionTemplate = (option: any) => {
    return option.label;
  };

  if (!preferredContactMethod || preferredContactMethod === "Email") {
    return null;
  }
  return (
    <StyledCard data-testid="communication" title="Text Message">
      <Controller
        name="sendDefaultText"
        control={control}
        render={({ field: { onChange, value } }) => (
          <SelectButton
            value={value}
            id="sendDefaultText"
            data-testid="sendDefaultText"
            onChange={(e) => {
              // Prevent deselecting - only change if a valid value is selected
              if (e.value !== null) {
                onChange(e.value);
              }
            }}
            options={options}
            itemTemplate={optionTemplate}
          />
        )}
      />
      {!defaultText && (
        <Controller
          name="customText"
          control={control}
          rules={{
            required: validationConstants.TEXT_MESSAGE_FORM.CUSTOM_TEXT,
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <>
              <InputTextarea
                id="customText"
                autoComplete="off"
                className="mt-3 w-full"
                value={value || ""}
                rows={3}
                aria-labelledby="customText"
                onBlur={() => {
                  handleOnBlurField({
                    onBlur,
                    field: `customText`,
                    trigger,
                  });
                }}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
              />

              {getFormErrorMessage("customText", errors)}
            </>
          )}
        />
      )}
    </StyledCard>
  );
}
